import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { z } from 'zod';
import { PosteSelector } from './PosteSelector';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useModalProtection } from '@/hooks/useModalProtection';

// Schema de validation Zod pour l'édition d'un membre
const membreEditSchema = z.object({
  prenom: z.string().min(1, 'Requis').max(100, '100 car. max'),
  nom: z.string().max(100, '100 car. max').optional(),
  poste: z.string().max(100, '100 car. max').optional(),
  specialite: z.string().max(100, '100 car. max').optional(),
  taux_horaire: z.number().positive('Requis').max(1000, 'Max 1000€/h'),
  coefficient_charges: z.number().min(1, 'Min 1.0').max(3, 'Max 3.0'),
  heures_par_jour: z.number().min(1, 'Min 1h').max(12, 'Max 12h'),
});

interface Equipe {
  id: string;
  nom: string;
  specialite?: string;
}

interface MembreToEdit {
  id: string;
  prenom: string;
  nom: string;
  poste: string | null;
  specialite: string | null;
  equipe_id: string | null;
  taux_horaire: number;
  coefficient_charges: number;
  heures_par_jour: number;
}

interface BulkMembreEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  membres: MembreToEdit[];
  equipes: Equipe[];
  onSuccess?: () => void;
}

export function BulkMembreEditModal({
  open,
  onOpenChange,
  membres,
  equipes,
  onSuccess,
}: BulkMembreEditModalProps) {
  useModalProtection(open);
  const [draft, setDraft] = useState<MembreToEdit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});

  // Initialiser le draft avec les membres à éditer
  useEffect(() => {
    if (open && membres.length > 0) {
      setDraft(membres.map(m => ({ ...m })));
      setErrors({});
    }
  }, [open, membres]);

  const handleChange = (index: number, data: Partial<MembreToEdit>) => {
    const updated = [...draft];
    updated[index] = { ...updated[index], ...data };
    setDraft(updated);

    // Clear error for this field
    if (errors[index]) {
      const newErrors = { ...errors };
      Object.keys(data).forEach(key => {
        delete newErrors[index]?.[key];
      });
      setErrors(newErrors);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<number, Record<string, string>> = {};
    let isValid = true;

    draft.forEach((row, index) => {
      const result = membreEditSchema.safeParse({
        prenom: row.prenom.trim(),
        nom: row.nom?.trim() || undefined,
        poste: row.poste?.trim() || undefined,
        specialite: row.specialite?.trim() || undefined,
        taux_horaire: row.taux_horaire || 0,
        coefficient_charges: row.coefficient_charges || 0,
        heures_par_jour: row.heures_par_jour || 0,
      });

      if (!result.success) {
        const rowErrors: Record<string, string> = {};
        result.error.issues.forEach(issue => {
          const field = issue.path[0] as string;
          rowErrors[field] = issue.message;
        });
        newErrors[index] = rowErrors;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);
    try {
      // Mettre à jour chaque membre
      const updates = draft.map(membre =>
        supabase
          .from('membres_equipe')
          .update({
            prenom: membre.prenom.trim(),
            nom: membre.nom.trim(),
            poste: membre.poste?.trim() || null,
            specialite: membre.specialite?.trim() || null,
            equipe_id: membre.equipe_id,
            taux_horaire: membre.taux_horaire,
            coefficient_charges: membre.coefficient_charges,
            heures_par_jour: membre.heures_par_jour,
          })
          .eq('id', membre.id)
      );

      const results = await Promise.all(updates);

      // Vérifier s'il y a des erreurs
      const hasErrors = results.some(r => r.error);
      if (hasErrors) {
        const errorMessages = results
          .filter(r => r.error)
          .map(r => r.error?.message)
          .join(', ');
        throw new Error(errorMessages);
      }

      toast.success(
        draft.length === 1
          ? 'Membre modifié avec succès'
          : `${draft.length} membres modifiés avec succès`
      );
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error('Erreur modification membres:', error);
      toast.error('Erreur lors de la modification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const isSingleEdit = draft.length === 1;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={isSingleEdit ? "max-w-4xl" : "max-w-6xl max-h-[85vh] overflow-hidden flex flex-col"}>
        <DialogHeader>
          <DialogTitle>
            {isSingleEdit ? 'Modifier le membre' : `Modifier ${draft.length} membres`}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {isSingleEdit
              ? 'Modifiez les informations du membre'
              : 'Modifiez les informations des membres sélectionnés'}
          </p>
        </DialogHeader>

        {/* En-têtes des colonnes */}
        <div className="grid grid-cols-[1fr_1fr_1fr_1fr_140px_100px_80px_70px] gap-2 px-3 text-xs text-muted-foreground font-medium">
          <span>Prénom *</span>
          <span>Nom</span>
          <span>Poste</span>
          <span>Spécialité</span>
          <span>Équipe</span>
          <span>Taux net *</span>
          <span>Coef *</span>
          <span>h/j</span>
        </div>

        {/* Liste des membres à modifier */}
        <div className={isSingleEdit ? "py-2" : "flex-1 overflow-y-auto space-y-2 py-2"}>
          {draft.map((row, index) => (
            <div
              key={row.id}
              className="grid grid-cols-[1fr_1fr_1fr_1fr_140px_100px_80px_70px] gap-2 items-start p-3 bg-card/50 rounded-lg border border-border/50"
            >
              {/* Prénom */}
              <div>
                <Input
                  placeholder="Prénom *"
                  value={row.prenom}
                  onChange={(e) => handleChange(index, { prenom: e.target.value })}
                  className={errors[index]?.prenom ? 'border-red-500' : ''}
                />
                {errors[index]?.prenom && <span className="text-xs text-red-500">{errors[index].prenom}</span>}
              </div>

              {/* Nom */}
              <Input
                placeholder="Nom"
                value={row.nom}
                onChange={(e) => handleChange(index, { nom: e.target.value })}
              />

              {/* Poste */}
              <PosteSelector
                value={row.poste || ''}
                onChange={(poste) => handleChange(index, { poste })}
              />

              {/* Spécialité */}
              <Input
                placeholder="Spécialité"
                value={row.specialite || ''}
                onChange={(e) => handleChange(index, { specialite: e.target.value })}
              />

              {/* Équipe */}
              <Select
                value={row.equipe_id || 'none'}
                onValueChange={(value) => handleChange(index, { equipe_id: value === 'none' ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Équipe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune</SelectItem>
                  {equipes.map((equipe) => (
                    <SelectItem key={equipe.id} value={equipe.id}>
                      {equipe.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Taux net horaire */}
              <div>
                <div className="relative">
                  <Input
                    type="number"
                    placeholder="Taux net"
                    value={row.taux_horaire || ''}
                    onChange={(e) => handleChange(index, { taux_horaire: parseFloat(e.target.value) || 0 })}
                    className={`pr-8 ${errors[index]?.taux_horaire ? 'border-red-500' : ''}`}
                    min={0}
                    step={0.5}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€/h</span>
                </div>
                {errors[index]?.taux_horaire && <span className="text-xs text-red-500">{errors[index].taux_horaire}</span>}
              </div>

              {/* Coefficient charges */}
              <div>
                <div className="relative">
                  <Input
                    type="number"
                    placeholder="Coef"
                    value={row.coefficient_charges || ''}
                    onChange={(e) => handleChange(index, { coefficient_charges: parseFloat(e.target.value) || 0 })}
                    className={`pr-5 ${errors[index]?.coefficient_charges ? 'border-red-500' : ''}`}
                    min={1}
                    max={3}
                    step={0.01}
                  />
                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">×</span>
                </div>
                {errors[index]?.coefficient_charges && <span className="text-xs text-red-500">{errors[index].coefficient_charges}</span>}
              </div>

              {/* Heures par jour */}
              <div>
                <Input
                  type="number"
                  placeholder="h/j"
                  value={row.heures_par_jour || ''}
                  onChange={(e) => handleChange(index, { heures_par_jour: parseFloat(e.target.value) || 0 })}
                  min={1}
                  max={12}
                  step={0.5}
                />
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? 'Modification...' : 'Modifier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
